import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Bars3Icon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { CellEntriesPopover } from './CellEntriesPopover';
import type { Entry, Project } from '../../services/api/timeEntry';
import type { PastedEntryData } from './MonthGrid';
import { useTrackedTimeout } from '../../hooks/useTrackedTimeout';
import { isMacPlatform } from '../../utils/platform';
import {
  CELL_STATE_CLASS,
  ROW_STATE_BORDER,
  ROW_STATE_LABEL,
  dominantRowState,
  formatHoursValue,
} from './totalsFormat';
import { LEGACY_CZ_NUMBER_LOCALE, compareText } from '../../utils/localeFormat';

interface TimeOffInfo {
  hours: number;
  typeName: string;
}

interface LockedPeriod {
  from: string;
  to: string;
  state: string;
}

interface DayGridProps {
  date: string; // YYYY-MM-DD
  entries: Entry[];
  projects: Project[];
  timeOffDays?: Map<string, TimeOffInfo>;
  showAllProjects?: boolean;
  onShowAllProjectsChange?: (show: boolean) => void;
  previousPeriodProjectIds?: Set<number>;
  projectOrder?: number[];
  onProjectOrderChange?: (order: number[]) => void;
  hiddenProjectIds?: Set<number>;
  onHideProject?: (projectId: number) => void;
  onUnhideProject?: (projectId: number) => void;
  lockedPeriods?: LockedPeriod[];
  readOnly?: boolean;
  onEditEntry: (entry: Entry) => void;
  onDeleteEntry?: (entry: Entry) => void;
  onCreateEntry: (date: string, projectId: number, pastedData?: PastedEntryData) => void;
  onMoveEntry?: (entryId: number, newDate: string, newProjectId: number) => void;
}

function isDateInLockedPeriod(dateStr: string, lockedPeriods: LockedPeriod[]): LockedPeriod | null {
  for (const period of lockedPeriods) {
    if (dateStr >= period.from && dateStr <= period.to) {
      return period;
    }
  }
  return null;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function DayGrid({
  date,
  entries,
  projects,
  timeOffDays = new Map(),
  showAllProjects = false,
  onShowAllProjectsChange,
  previousPeriodProjectIds = new Set(),
  projectOrder = [],
  onProjectOrderChange,
  hiddenProjectIds = new Set(),
  onHideProject,
  onUnhideProject,
  lockedPeriods = [],
  readOnly = false,
  onEditEntry,
  onDeleteEntry,
  onCreateEntry,
  onMoveEntry,
}: DayGridProps) {
  // Group entries by project
  const entryMap = useMemo(() => {
    const map = new Map<number, Entry[]>();
    for (const entry of entries) {
      const existing = map.get(entry.projectId) || [];
      existing.push(entry);
      map.set(entry.projectId, existing);
    }
    return map;
  }, [entries]);

  // Base projects (from entries + defaults)
  const baseProjects = useMemo(() => {
    const projectIds = new Set<number>();
    const result: Project[] = [];

    for (const entry of entries) {
      if (!projectIds.has(entry.projectId)) {
        projectIds.add(entry.projectId);
        const project = projects.find((p) => p.id === entry.projectId);
        if (project) {
          result.push(project);
        } else {
          result.push({
            id: entry.projectId,
            name: entry.project.name,
            fullName: entry.project.fullName,
            state: 'in_progress',
            isDefault: false,
            mandatoryTasks: false,
            defaultRate: null,
            customer: null,
          });
        }
      }
    }

    for (const project of projects) {
      if (project.isDefault && !projectIds.has(project.id)) {
        projectIds.add(project.id);
        result.push(project);
      }
    }

    return result;
  }, [entries, projects]);

  const currentDayProjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const entry of entries) {
      if (entry.hours > 0) ids.add(entry.projectId);
    }
    return ids;
  }, [entries]);

  // Filter — same as MonthGrid
  const filteredProjects = useMemo(() => {
    if (showAllProjects) {
      const projectIds = new Set(baseProjects.map((p) => p.id));
      const allProjects = [...baseProjects];
      for (const project of projects) {
        if (!projectIds.has(project.id)) allProjects.push(project);
      }
      return allProjects;
    }
    const seen = new Set<number>();
    const result = baseProjects.filter((p) => {
      if (currentDayProjectIds.has(p.id) || previousPeriodProjectIds.has(p.id)) {
        seen.add(p.id);
        return true;
      }
      return false;
    });
    for (const project of projects) {
      if (!seen.has(project.id) && previousPeriodProjectIds.has(project.id)) result.push(project);
    }
    return result;
  }, [baseProjects, projects, showAllProjects, currentDayProjectIds, previousPeriodProjectIds]);

  const visibleProjects = useMemo(() => {
    return filteredProjects.filter((p) => {
      if (currentDayProjectIds.has(p.id)) return true;
      if (readOnly) return false;
      if (hiddenProjectIds.has(p.id) && !showAllProjects) return false;
      return true;
    });
  }, [filteredProjects, hiddenProjectIds, currentDayProjectIds, showAllProjects, readOnly]);

  const displayProjects = useMemo(() => {
    const isHiddenDimmed = (p: Project) =>
      showAllProjects && hiddenProjectIds.has(p.id) && !currentDayProjectIds.has(p.id);

    if (projectOrder.length === 0) {
      return [...visibleProjects].sort((a, b) => {
        const aH = isHiddenDimmed(a) ? 1 : 0;
        const bH = isHiddenDimmed(b) ? 1 : 0;
        if (aH !== bH) return aH - bH;
        return compareText((a.fullName || a.name || ''), b.fullName || b.name || '', LEGACY_CZ_NUMBER_LOCALE);
      });
    }
    const orderMap = new Map(projectOrder.map((id, i) => [id, i]));
    return [...visibleProjects].sort((a, b) => {
      const aH = isHiddenDimmed(a) ? 1 : 0;
      const bH = isHiddenDimmed(b) ? 1 : 0;
      if (aH !== bH) return aH - bH;
      const oA = orderMap.get(a.id);
      const oB = orderMap.get(b.id);
      if (oA !== undefined && oB !== undefined) return oA - oB;
      if (oA !== undefined) return -1;
      if (oB !== undefined) return 1;
      return compareText((a.fullName || a.name || ''), b.fullName || b.name || '', LEGACY_CZ_NUMBER_LOCALE);
    });
  }, [visibleProjects, projectOrder, showAllProjects, hiddenProjectIds, currentDayProjectIds]);

  // Drag state for project reordering
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, projectId: number) => {
    setDraggedId(projectId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(projectId));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, projectId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (projectId !== draggedId) setDragOverId(projectId);
  }, [draggedId]);

  const handleDragLeave = useCallback(() => { setDragOverId(null); }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetId) { setDraggedId(null); setDragOverId(null); return; }
    const currentOrder = displayProjects.map((p) => p.id);
    const di = currentOrder.indexOf(draggedId);
    const ti = currentOrder.indexOf(targetId);
    if (di === -1 || ti === -1) { setDraggedId(null); setDragOverId(null); return; }
    const newOrder = [...currentOrder];
    newOrder.splice(di, 1);
    newOrder.splice(ti, 0, draggedId);
    onProjectOrderChange?.(newOrder);
    setDraggedId(null);
    setDragOverId(null);
  }, [draggedId, displayProjects, onProjectOrderChange]);

  const handleDragEnd = useCallback(() => { setDraggedId(null); setDragOverId(null); }, []);

  // Entry drag state (drag entry between projects on same day)
  const [draggedEntry, setDraggedEntry] = useState<Entry | null>(null);
  const [dropTargetProjectId, setDropTargetProjectId] = useState<number | null>(null);

  const handleEntryDragStart = useCallback(
    (e: React.DragEvent, entry: Entry) => {
      if (readOnly || !onMoveEntry) return;
      e.stopPropagation();
      setDraggedEntry(entry);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', entry.id.toString());
    },
    [readOnly, onMoveEntry]
  );

  const handleCellDragOver = useCallback(
    (e: React.DragEvent, projectId: number) => {
      if (!draggedEntry || readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      setDropTargetProjectId(projectId);
    },
    [draggedEntry, readOnly]
  );

  const handleCellDragLeave = useCallback(() => {
    setDropTargetProjectId(null);
  }, []);

  const handleCellDrop = useCallback(
    (e: React.DragEvent, projectId: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggedEntry || !onMoveEntry) return;
      if (draggedEntry.projectId !== projectId) {
        onMoveEntry(draggedEntry.id, date, projectId);
      }
      setDraggedEntry(null);
      setDropTargetProjectId(null);
    },
    [draggedEntry, onMoveEntry, date]
  );

  const handleEntryDragEnd = useCallback(() => {
    setDraggedEntry(null);
    setDropTargetProjectId(null);
  }, []);

  // Popover state
  const [popoverState, setPopoverState] = useState<{
    isOpen: boolean; date: string; projectId: number; projectName: string;
    entries: Entry[]; position: { top: number; left: number }; isLocked: boolean;
  } | null>(null);

  // Keyboard nav
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const cellRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const tableRef = useRef<HTMLDivElement>(null);
  // F1-009: track row-swap focus-follow timeout so unmount / re-keys
  // cannot leave a pending .focus() targeting a stale ref.
  const focusFollow = useTrackedTimeout();
  const [copiedEntry, setCopiedEntry] = useState<PastedEntryData | null>(null);

  const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
  const isLocked = !!lockedPeriod && lockedPeriod.state !== 'rejected';

  const handleCellClick = useCallback(
    (e: React.MouseEvent | { currentTarget: HTMLElement }, projectId: number, project: Project) => {
      const cellEntries = entryMap.get(projectId) || [];
      if (cellEntries.length === 0) {
        if (!readOnly && !isLocked) onCreateEntry(date, projectId);
        return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      setPopoverState({
        isOpen: true, date, projectId,
        projectName: project.fullName || project.name,
        entries: cellEntries,
        position: { top: rect.bottom + 4, left: Math.max(8, rect.left - 100) },
        isLocked,
      });
    },
    [entryMap, readOnly, isLocked, onCreateEntry, date]
  );

  const handlePopoverClose = useCallback(() => { setPopoverState(null); }, []);
  const handlePopoverEdit = useCallback((entry: Entry) => {
    if (readOnly) return; setPopoverState(null); onEditEntry(entry);
  }, [onEditEntry, readOnly]);
  const handlePopoverDelete = useCallback((entry: Entry) => {
    if (readOnly || !onDeleteEntry) return; setPopoverState(null); onDeleteEntry(entry);
  }, [onDeleteEntry, readOnly]);
  const handlePopoverAdd = useCallback(() => {
    if (readOnly || !popoverState) return; setPopoverState(null); onCreateEntry(popoverState.date, popoverState.projectId);
  }, [popoverState, onCreateEntry, readOnly]);

  // Keyboard handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (displayProjects.length === 0) return;
      const maxRow = displayProjects.length - 1;
      const modKey = isMacPlatform() ? e.metaKey : e.ctrlKey;

      if (modKey && activeRow !== null) {
        const project = displayProjects[activeRow];
        if (!project) return;
        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault(); e.stopPropagation();
          const ce = entryMap.get(project.id) || [];
          if (ce[0]) setCopiedEntry({ hours: ce[0].hours, description: ce[0].description, task: ce[0].task });
          cellRefs.current.get(String(activeRow))?.focus();
          return;
        }
        if (e.key === 'v' || e.key === 'V') {
          e.preventDefault(); e.stopPropagation();
          if (copiedEntry && !readOnly && !isLocked) onCreateEntry(date, project.id, copiedEntry);
          return;
        }
      }

      if ((e.key === 'Backspace' || e.key === 'Delete' || e.key === 'd' || e.key === 'D') && activeRow !== null && onDeleteEntry && !readOnly) {
        const project = displayProjects[activeRow];
        if (!project || isLocked) return;
        const deletable = (entryMap.get(project.id) || []).filter(en => en.state === 'open' || en.state === 'rejected');
        if (deletable.length > 0) { e.preventDefault(); e.stopPropagation(); for (const en of deletable) onDeleteEntry(en); }
        return;
      }

      if (activeRow === null) {
        if (['ArrowUp', 'ArrowDown'].includes(e.key)) { e.preventDefault(); setActiveRow(0); cellRefs.current.get('0')?.focus(); }
        return;
      }

      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const target = e.key === 'ArrowUp' ? activeRow - 1 : activeRow + 1;
        if (target < 0 || target > maxRow) return;
        const cur = displayProjects.map((p) => p.id);
        const nw = [...cur]; const tmp = nw[activeRow]!; nw[activeRow] = nw[target]!; nw[target] = tmp;
        onProjectOrderChange?.(nw); setActiveRow(target);
        focusFollow.schedule(() => {
          cellRefs.current.get(String(target))?.focus();
        }, 0);
        return;
      }

      switch (e.key) {
        case 'ArrowUp': e.preventDefault(); { const r = Math.max(0, activeRow - 1); setActiveRow(r); cellRefs.current.get(String(r))?.focus(); } break;
        case 'ArrowDown': e.preventDefault(); { const r = Math.min(maxRow, activeRow + 1); setActiveRow(r); cellRefs.current.get(String(r))?.focus(); } break;
        case 'Enter': case ' ': {
          e.preventDefault();
          const project = displayProjects[activeRow];
          if (!project) return;
          const cellEl = cellRefs.current.get(String(activeRow));
          if (cellEl) handleCellClick({ currentTarget: cellEl }, project.id, project);
          return;
        }
        case 'Escape': e.preventDefault(); setActiveRow(null); return;
      }
    },
    [activeRow, displayProjects, entryMap, copiedEntry, onCreateEntry, onDeleteEntry, readOnly, isLocked, date, handleCellClick, onProjectOrderChange, focusFollow]
  );

  useEffect(() => {
    const c = tableRef.current;
    if (!c) return;
    c.addEventListener('keydown', handleKeyDown as EventListener);
    return () => { c.removeEventListener('keydown', handleKeyDown as EventListener); };
  }, [handleKeyDown]);

  const timeOff = timeOffDays.get(date);
  const dayTotal = entries.reduce((sum, e) => sum + e.hours, 0) + (timeOff ? timeOff.hours : 0);

  // Day header info
  const dateObj = new Date(date + 'T00:00:00');
  const dayOfWeek = dateObj.getDay();
  const dayNum = parseInt(date.split('-')[2] || '1', 10);
  const todayDate = new Date();
  const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
  const isToday = date === todayStr;

  return (
    <div className="flex gap-4 max-h-[calc(100vh-12rem)]">
      {/* Left 1/3: time entry table */}
      <div className="w-1/3 min-w-[400px] overflow-auto relative outline-none" ref={tableRef} tabIndex={0}>
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 z-20">
          <tr className="bg-white">
            <th className="py-1 px-2 text-left font-medium text-gray-700 w-[280px] max-w-[280px] sticky left-0 bg-white z-30">
              <div className="flex items-center justify-between">
                <span>Project</span>
                <label className={`flex items-center gap-1 text-[10px] font-normal cursor-pointer ${
                  displayProjects.length === 0 && !showAllProjects ? 'text-blue-600 font-medium' : 'text-gray-500'
                }`}>
                  <input
                    type="checkbox"
                    checked={showAllProjects}
                    onChange={(e) => onShowAllProjectsChange?.(e.target.checked)}
                    className={`h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                      displayProjects.length === 0 && !showAllProjects ? 'animate-pulse ring-2 ring-blue-400 ring-offset-1' : ''
                    }`}
                  />
                  All
                </label>
              </div>
            </th>
            <th className="py-1 px-1 bg-white w-16">
              <div className={`text-center ${isToday ? 'text-blue-600' : ''}`}>
                <div className="text-[10px]">{DAY_NAMES[dayOfWeek]}</div>
                <div className={`text-xs ${isToday ? 'bg-blue-600 text-white rounded-full w-5 h-5 mx-auto flex items-center justify-center' : ''}`}>
                  {dayNum}
                </div>
              </div>
            </th>
            <th className="py-1 px-2 text-right font-medium text-gray-700 w-16 sticky right-0 bg-white z-30">
              Total
            </th>
          </tr>
          <tr className="border-b border-gray-200">
            <td colSpan={3}></td>
          </tr>
        </thead>
        <tbody>
          {/* Time Off */}
          {timeOff && (
            <tr className="border-b-2 border-indigo-200 bg-indigo-50">
              <td className="py-1 px-2 font-medium text-indigo-700 text-xs sticky left-0 bg-indigo-50 z-10">
                Time Off
              </td>
              <td className="py-0.5 px-1">
                <div
                  className="w-full h-7 flex items-center justify-center text-[10px] font-medium bg-indigo-200 text-indigo-800 rounded-sm"
                  title={`${timeOff.typeName}: ${timeOff.hours}h`}
                >
                  {timeOff.hours}
                </div>
              </td>
              <td className="py-1 px-2 text-right font-medium text-indigo-700 text-xs sticky right-0 bg-indigo-50 z-10">
                {timeOff.hours > 0 ? timeOff.hours.toFixed(1) : '-'}
              </td>
            </tr>
          )}
          {displayProjects.map((project, rowIndex) => {
            const projectEntries = entryMap.get(project.id) || [];
            const cellTotal = projectEntries.reduce((s, e) => s + e.hours, 0);
            const hasMultiple = projectEntries.length > 1;
            const isDragging = draggedId === project.id;
            const isDragOver = dragOverId === project.id;
            const isEvenRow = rowIndex % 2 === 0;
            const rowBg = isEvenRow ? 'bg-white' : 'bg-gray-100';
            const isHiddenDimmed = showAllProjects && hiddenProjectIds.has(project.id) && !currentDayProjectIds.has(project.id);
            const allEditable = projectEntries.every((e) => e.state === 'open' || e.state === 'rejected');
            const isActiveRow = activeRow === rowIndex;
            const isDropTarget = dropTargetProjectId === project.id;
            const isDraggingThis = draggedEntry && projectEntries.some(e => e.id === draggedEntry.id);
            const canDragEntry = !readOnly && !isLocked && projectEntries.length === 1 && allEditable && !!onMoveEntry;
            const draggableEntry = canDragEntry ? projectEntries[0] : undefined;
            // MKS-315 — row state as a thin left border, lighter cell fill.
            const rowState = dominantRowState(projectEntries);
            const stateClass = rowState ? CELL_STATE_CLASS[rowState] : '';

            return (
              <tr
                key={project.id}
                onDragOver={readOnly ? undefined : (e) => handleDragOver(e, project.id)}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDrop={readOnly ? undefined : (e) => handleDrop(e, project.id)}
                className={`group/row border-b border-gray-100 hover:bg-blue-50/50 ${rowBg} ${
                  isDragging ? 'opacity-50' : ''
                } ${isDragOver ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
              >
                <td
                  draggable={!readOnly}
                  onDragStart={readOnly ? undefined : (e) => handleDragStart(e, project.id)}
                  onDragEnd={readOnly ? undefined : handleDragEnd}
                  className={`py-1 px-2 w-[280px] max-w-[280px] sticky left-0 z-10 ${isEvenRow ? 'bg-white' : 'bg-gray-100'} ${
                    rowState ? `border-l-2 ${ROW_STATE_BORDER[rowState]}` : ''
                  }`}
                  title={project.fullName || project.name}
                >
                  <div className="flex items-center gap-1">
                    {rowState && <span className="sr-only">{ROW_STATE_LABEL[rowState]}</span>}
                    {!readOnly && (
                      <Bars3Icon className="w-3 h-3 text-gray-400 cursor-grab flex-shrink-0" />
                    )}
                    <span className={`font-medium truncate block ${isHiddenDimmed ? 'text-gray-400' : 'text-gray-900'}`}>
                      {project.fullName || project.name}
                    </span>
                    {!readOnly && isHiddenDimmed && onUnhideProject && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onUnhideProject(project.id); }}
                        className="ml-auto flex-shrink-0 p-0.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                        title="Show project"
                      >
                        <EyeIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {!readOnly && cellTotal === 0 && !hiddenProjectIds.has(project.id) && onHideProject && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onHideProject(project.id); }}
                        className="ml-auto flex-shrink-0 p-0.5 text-gray-300 hover:text-red-500 rounded transition-colors opacity-0 group-hover/row:opacity-100"
                        title="Hide project"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
                <td
                  className="py-0.5 px-0.5 overflow-visible relative"
                  onDragOver={draggedEntry ? (e) => handleCellDragOver(e, project.id) : undefined}
                  onDragLeave={draggedEntry ? handleCellDragLeave : undefined}
                  onDrop={draggedEntry ? (e) => handleCellDrop(e, project.id) : undefined}
                >
                  <button
                    ref={(el) => {
                      if (el) cellRefs.current.set(String(rowIndex), el);
                      else cellRefs.current.delete(String(rowIndex));
                    }}
                    tabIndex={isActiveRow ? 0 : -1}
                    draggable={canDragEntry}
                    onDragStart={draggableEntry ? (e) => handleEntryDragStart(e, draggableEntry) : undefined}
                    onDragEnd={canDragEntry ? handleEntryDragEnd : undefined}
                    onClick={() => { setActiveRow(rowIndex); }}
                    onDoubleClick={(e) => { handleCellClick(e, project.id, project); }}
                    onFocus={() => setActiveRow(rowIndex)}
                    disabled={(isLocked || readOnly) && projectEntries.length === 0}
                    className={`
                      w-full h-7 text-center text-xs rounded-sm transition-colors relative overflow-visible
                      ${projectEntries.length > 0 ? 'font-medium' : ''}
                      ${stateClass}
                      ${projectEntries.length === 0 && !isLocked && !readOnly ? 'hover:bg-gray-100' : ''}
                      ${(isLocked || readOnly) && projectEntries.length === 0 ? 'cursor-default' : 'cursor-pointer'}
                      ${isDropTarget && !isLocked ? 'ring-2 ring-green-500 bg-green-50' : ''}
                      ${isDraggingThis ? 'opacity-50' : ''}
                      ${canDragEntry ? 'cursor-grab active:cursor-grabbing' : ''}
                      ${isActiveRow ? 'ring-2 ring-indigo-500 ring-offset-1 z-30' : ''}
                    `}
                    title={projectEntries.length > 0
                      ? projectEntries.map(e => `${e.hours}h${e.task ? ` - ${e.task}` : ''}${e.description ? `: ${e.description}` : ''}`).join('\n')
                      : date}
                  >
                    {cellTotal > 0 ? cellTotal : ''}
                    {hasMultiple && (
                      <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center leading-none font-medium z-20">
                        {projectEntries.length}
                      </span>
                    )}
                  </button>
                </td>
                <td className={`py-1 px-2 text-right font-medium text-gray-900 text-xs sticky right-0 z-10 ${isEvenRow ? 'bg-white' : 'bg-gray-100'}`}>
                  {cellTotal > 0 ? cellTotal.toFixed(1) : '-'}
                </td>
              </tr>
            );
          })}

          {displayProjects.length === 0 && !timeOff && (
            <tr>
              <td colSpan={3} className="py-6 text-center text-gray-500 text-sm">
                {!showAllProjects && baseProjects.length > 0
                  ? 'No active projects. Check "All" to see projects assigned to everyone.'
                  : 'No projects assigned. Contact your manager to get assigned to projects.'}
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-200 bg-gray-50">
            <td className="py-1 px-2 font-semibold text-gray-900 text-xs sticky left-0 bg-gray-50 z-10">
              Daily Total
            </td>
            <td className="py-1 px-1 text-center font-semibold text-xs text-gray-900">
              {dayTotal > 0 ? dayTotal.toFixed(1) : '-'}
            </td>
            {/* MKS-315 — the unit, so the number reads the same way as the
                logged-vs-target totals in the week and month views. */}
            <td className="py-1 px-2 text-right font-bold text-xs text-blue-600 sticky right-0 bg-gray-50 z-10 whitespace-nowrap">
              {`${formatHoursValue(dayTotal)} h`}
            </td>
          </tr>
        </tfoot>
      </table>

      {popoverState?.isOpen && (
        <CellEntriesPopover
          entries={popoverState.entries}
          projectName={popoverState.projectName}
          date={popoverState.date}
          isLocked={popoverState.isLocked}
          position={popoverState.position}
          onEdit={handlePopoverEdit}
          onDelete={handlePopoverDelete}
          onAdd={handlePopoverAdd}
          onClose={handlePopoverClose}
          readOnly={readOnly}
        />
      )}
      </div>

      {/* Right 2/3: placeholder for future image/content */}
      <div className="w-2/3 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400 text-sm">
      </div>
    </div>
  );
}
